import { Component, signal, ViewEncapsulation, AfterViewInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

// Lightweight types
type JsonValue = null | string | number | boolean | JsonObject | JsonArray;
interface JsonObject { [k: string]: JsonValue; }
interface JsonArray extends Array<JsonValue> {}

interface MindmapFilters { mode: 'nameDescOnly' | 'everything'; }

@Component({
  selector: 'app-json-viewer',
  imports: [],
  templateUrl: './json-viewer.component.html',
  styleUrls: ['./json-viewer.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class JsonViewerComponent implements AfterViewInit, OnDestroy {
  data = signal<string | null>(null);

  // Internal state mirrors original script
  private ROOT: JsonValue = null;           // Full JSON
  private CURRENT: JsonValue = null;        // Current working dataset (full or subset)
  private SELECTED: { path: (string | number)[] | null; value: JsonValue } = { path: null, value: null };
  private lastSelected: HTMLElement | null = null;
  private MINDMAP_FILTERS: MindmapFilters = { mode: 'nameDescOnly' };
  private destroyFns: (() => void)[] = [];

  constructor(private route: ActivatedRoute) {
    // Blob URL support
    this.route.queryParams.subscribe(params => {
      const blobUrl = params['blobUrl'];
      if (blobUrl) {
        fetch(blobUrl)
          .then(r => r.text())
          .then(txt => {
            this.data.set(txt);
            this.tryParseRoot(txt);
          })
          .catch(err => console.error('Error fetching file content:', err));
      }
    });

  // (Optional) If data is set externally after construction, a consumer can call refreshFromSignal()
  }
  // Allow external triggers if data signal updated after initialization
  refreshFromSignal() {
    const txt = this.data();
    if (txt) this.tryParseRoot(txt);
  }

  ngAfterViewInit(): void {
    // Seed tiny sample so UI shows before user loads anything
    const sample = {
      "study": {
        "id": "Study_000001",
        "versions": [
          { "studyDesigns": [ { "arms": [ {"id": "Arm_1", "name": "A"}, {"id": "Arm_2", "name": "B"} ], "epochs": [ {"id": "E1"}, {"id": "E2"} ] } ] }
        ]
      }
    };
    if (!this.ROOT) {
      this.ROOT = sample;
      this.CURRENT = this.ROOT;
      this.renderTree(this.CURRENT);
    }
    this.wireDom();
  }

  ngOnDestroy(): void {
    this.destroyFns.forEach(fn => { try { fn(); } catch { /* noop */ } });
  }

  private tryParseRoot(txt: string) {
    try {
      this.ROOT = JSON.parse(txt);
      this.CURRENT = this.ROOT;
      this.renderTree(this.CURRENT);
      this.renderMind(this.CURRENT);
    } catch (e) {
      console.error('Invalid JSON supplied to viewer:', e);
    }
  }

  /*** Utilities (ported) ***/
  private isPrim(v: JsonValue): v is (null | string | number | boolean) {
    return v === null || typeof v !== 'object';
  }

  private typeCls(v: JsonValue): string {
    if (v === null) return 'type-null';
    switch (typeof v) {
      case 'number': return 'type-number';
      case 'boolean': return 'type-boolean';
      case 'string': return 'type-string';
      default: return 'type-obj';
    }
  }

  private shouldIncludeInMindmap(key: string): boolean {
    const keyLower = key.toLowerCase();
    if (this.MINDMAP_FILTERS.mode === 'nameDescOnly') {
      return keyLower === 'name' || keyLower === 'description' || keyLower === 'text';
    }
    return true;
  }

  private getFilteredKeys(obj: JsonObject): string[] {
    return Object.keys(obj).filter(k => this.shouldIncludeInMindmap(k));
  }

  private pathToString(path: (string | number)[]): string {
    return path.map(p => typeof p === 'number' ? `[${p}]` : (/^[A-Za-z_]\w*$/.test(p) ? `.${p}` : `[${JSON.stringify(p)}]`))
      .join('').replace(/^\./, '');
  }

  private getByPath(obj: JsonValue, pathStr: string): JsonValue | undefined {
    if (!pathStr) return obj;
    const parts: (string | number)[] = [];
    let i = 0; const s = pathStr.trim();
    while (i < s.length) {
      if (s[i] === '.') { i++; continue; }
      if (s[i] === '[') {
        const close = s.indexOf(']', i);
        if (close === -1) break;
        const inside = s.slice(i + 1, close);
        let key: string | number;
        if (/^\d+$/.test(inside)) key = parseInt(inside, 10); else key = JSON.parse(inside);
        parts.push(key);
        i = close + 1;
      } else {
  const match = /[.[]/.exec(s.slice(i));
        const end = match ? i + match.index : s.length;
        parts.push(s.slice(i, end));
        i = end;
      }
    }
    let acc: JsonValue | undefined = obj;
    for (const k of parts) {
      if (acc == null || typeof acc !== 'object') return undefined;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      acc = (acc as any)[k];
    }
    return acc;
  }

  /*** TREE (lazy) ***/
  private createNode(opts: { keyName: string | number | null; value: JsonValue; path: (string | number)[]; open?: boolean; depth: number }): HTMLElement {
    const { keyName, value, path } = opts;
    const open = opts.open ?? false;
    const depth = opts.depth;
    const node = document.createElement('div'); node.className = 'node';
    const row = document.createElement('div'); row.className = 'rowline';
    const hasChildren = !this.isPrim(value) && (Array.isArray(value) ? value.length > 0 : Object.keys(value || {}).length > 0);
    const twisty = document.createElement('span'); twisty.className = 'twisty'; twisty.textContent = open ? '−' : '+'; if (!hasChildren) twisty.style.visibility = 'hidden';
    const keySpan = document.createElement('span'); keySpan.className = 'key'; if (keyName !== null) keySpan.textContent = JSON.stringify(keyName);
    const colon = document.createElement('span'); colon.className = 'colon'; colon.textContent = keyName !== null ? ':' : '';
    const val = document.createElement('span'); val.className = this.typeCls(value);
    if (this.isPrim(value)) {
      val.textContent = JSON.stringify(value);
    } else if (Array.isArray(value)) {
      val.innerHTML = `<span class="meta">[${value.length}]</span>`;
    } else {
      val.innerHTML = `<span class="meta">{${Object.keys(value as JsonObject).length}}</span>`;
    }
    row.append(twisty); if (keyName !== null) row.append(keySpan, colon); row.append(val); node.append(row);
    const kids = document.createElement('div'); kids.className = 'children';
    const build = () => {
      if (this.isPrim(value)) return;
      kids.innerHTML = '';
      if (Array.isArray(value)) {
        value.forEach((v, i) => kids.appendChild(this.createNode({ keyName: i, value: v, path: path.concat([i]), depth: depth + 1, open: depth < 1 })));
      } else if (value && typeof value === 'object') {
        Object.keys(value).forEach(k => kids.appendChild(this.createNode({ keyName: k, value: (value as JsonObject)[k], path: path.concat([k]), depth: depth + 1, open: depth < 1 })));
      }
      node.appendChild(kids);
    };
    if (open && hasChildren) build();
    twisty.onclick = e => {
      e.stopPropagation();
      if (kids.parentNode) { twisty.textContent = '+'; kids.remove(); }
      else { twisty.textContent = '−'; if (!kids.childElementCount) build(); else node.appendChild(kids); }
    };
    row.onclick = e => {
      e.stopPropagation();
      this.SELECTED = { path, value };
      if (this.lastSelected) this.lastSelected.classList.remove('selected');
      node.classList.add('selected'); this.lastSelected = node;
    };
    return node;
  }

  private renderTree(data: JsonValue) {
    const treeEl = document.getElementById('tree');
    if (!treeEl) return;
    treeEl.innerHTML = '';
    treeEl.appendChild(this.createNode({ keyName: null, value: data, path: [], open: true, depth: 0 }));
  }

  /*** SUBSETTING ***/
  private subsetSelected() {
    if (!this.SELECTED || this.SELECTED.value === undefined) { alert('Select a node first'); return; }
    this.CURRENT = this.SELECTED.value;
    this.renderTree(this.CURRENT);
    this.renderMind(this.CURRENT);
  }

  private collectByKey(obj: JsonValue, key: string, out: JsonValue[]): JsonValue[] {
    if (Array.isArray(obj)) obj.forEach(v => this.collectByKey(v, key, out));
    else if (obj && typeof obj === 'object') {
      for (const k of Object.keys(obj)) {
        if (k === key) out.push((obj as JsonObject)[k]);
        this.collectByKey((obj as JsonObject)[k], key, out);
      }
    }
    return out;
  }

  private subsetByPath(p: string) {
    const sub = this.getByPath(this.ROOT, p);
    if (sub === undefined) { alert('Path not found'); return; }
    this.CURRENT = sub;
    this.renderTree(this.CURRENT);
    this.renderMind(this.CURRENT);
  }

  /*** MIND-MAP (simple layered layout) ***/
  private renderMind(data: JsonValue) {
    const svg = document.getElementById('mind') as SVGSVGElement | null;
    if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    if (data == null) return;

    // Direct port of original layout logic
    const boxW = 280, boxH = 60, xGap = 300, yGap = 8, lineHeight = 12, maxChars = 35, maxLines = 4;
    let yCursor = 10; let maxX = 0;

    const drawBox = (x: number, y: number, label: string, meta = false) => {
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', String(x)); rect.setAttribute('y', String(y));
      rect.setAttribute('rx', '8'); rect.setAttribute('ry', '8');
      rect.setAttribute('width', String(boxW)); rect.setAttribute('height', String(boxH));
      rect.setAttribute('class', 'mm-box');
      g.appendChild(rect);
      const words = label.split(' ');
      const lines: string[] = [];
      let currentLine = '';
      for (const word of words) {
        const testLine = currentLine ? currentLine + ' ' + word : word;
        if (testLine.length <= maxChars) currentLine = testLine; else {
          if (currentLine) lines.push(currentLine);
            currentLine = word;
            if (lines.length >= maxLines - 1) break;
        }
      }
      if (currentLine) lines.push(currentLine);
      if (lines.length > maxLines) {
        lines[maxLines - 1] = lines[maxLines - 1].slice(0, maxChars - 3) + '...';
        lines.splice(maxLines);
      }
      lines.forEach((ln, i) => {
        const t = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        t.setAttribute('x', String(x + 8));
        t.setAttribute('y', String(y + 16 + (i * lineHeight)));
        t.setAttribute('class', 'mm-text');
        t.setAttribute('font-size', '11');
        if (meta) t.setAttribute('fill', '#b7c4ff');
        t.textContent = ln;
        g.appendChild(t);
      });
      svg.appendChild(g);
      maxX = Math.max(maxX, x + boxW);
    };

    const line = (x1: number, y1: number, x2: number, y2: number) => {
      const ln = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      ln.setAttribute('x1', String(x1)); ln.setAttribute('y1', String(y1));
      ln.setAttribute('x2', String(x2)); ln.setAttribute('y2', String(y2));
      ln.setAttribute('stroke', '#4a58b8'); ln.setAttribute('stroke-width', '1.5');
      svg.appendChild(ln);
      maxX = Math.max(maxX, x1, x2);
    };

    const measure = (node: JsonValue): number => {
      if (this.isPrim(node)) return 1;
      if (Array.isArray(node)) {
        let total = 0; for (const v of node) total += measure(v); return total || 1;
      }
      const obj = node as JsonObject;
      const keys = Object.keys(obj);
      if (keys.length === 0) return 1;
      const keysToShow = this.getFilteredKeys(obj);
      let total = 0; for (const k of keysToShow) total += measure(obj[k]);
      return total || 1;
    };

  const layout = (node: JsonValue, depth: number, x: number, path: (string|number)[]) => {
      const rows = measure(node);
      const centerY = yCursor + (rows * (boxH + yGap) - (boxH + yGap)) / 2;
      let label: string;
      if (this.isPrim(node)) label = String(node);
      else if (Array.isArray(node)) label = `[${node.length}]`;
      else label = `{${Object.keys(node as JsonObject).length}}`;
      drawBox(x, centerY, label, true);
      // children
      if (this.isPrim(node)) { yCursor += (boxH + yGap); return; }
      const childX = x + xGap;
      if (Array.isArray(node)) {
        for (let i = 0; i < node.length; i++) {
          const subRows = measure(node[i]);
            const childY = yCursor + (subRows * (boxH + yGap) - (boxH + yGap)) / 2;
            line(x + boxW, centerY + boxH / 2, childX, childY + boxH / 2);
            drawBox(childX, childY, `#${i}`);
            layout(node[i], depth + 2, childX + xGap, path.concat([i]));
            yCursor += subRows * (boxH + yGap);
        }
      } else {
        const keysToShow = this.getFilteredKeys(node as JsonObject);
        for (const k of keysToShow) {
          const v = (node as JsonObject)[k];
          const subRows = measure(v);
          const childY = yCursor + (subRows * (boxH + yGap) - (boxH + yGap)) / 2;
          line(x + boxW, centerY + boxH / 2, childX, childY + boxH / 2);
          drawBox(childX, childY, k);
          layout(v, depth + 2, childX + xGap, path.concat([k]));
          yCursor += subRows * (boxH + yGap);
        }
      }
    };

    yCursor = 10;
  layout(data, 0, 20, []);
    const finalWidth = Math.max(800, maxX + 20);
    const finalHeight = Math.max(300, yCursor + 20);
    svg.setAttribute('width', String(finalWidth));
    svg.setAttribute('height', String(finalHeight));
  }

  /*** DOM wiring ***/
  private wireDom() {
    const fileInput = document.getElementById('file') as HTMLInputElement | null;
    const applyKeyBtn = document.getElementById('applyKey');
    const keyFilter = document.getElementById('keyFilter') as HTMLInputElement | null;
    const resetBtn = document.getElementById('reset');
    const nameDescOnly = document.getElementById('nameDescOnly');
    const showEverything = document.getElementById('showEverything');

    // Apply key filter
    applyKeyBtn?.addEventListener('click', () => {
      const key = keyFilter?.value.trim();
      if (!key) { alert('Enter a key name'); return; }
      const list = this.collectByKey(this.ROOT, key, []);
      this.CURRENT = list.length === 1 ? list[0] : list;
      this.renderTree(this.CURRENT);
      this.renderMind(this.CURRENT);
    });

    // Common pills
    document.querySelectorAll('.pill.common').forEach(pill => {
      pill.addEventListener('click', () => {
        const key = (pill as HTMLElement).dataset['key'];
        if (!key) return;
        const list = this.collectByKey(this.ROOT, key, []);
        this.CURRENT = list.length === 1 ? list[0] : list;
        this.renderTree(this.CURRENT);
        this.renderMind(this.CURRENT);
      });
    });

    // Reset
    resetBtn?.addEventListener('click', () => {
      this.CURRENT = this.ROOT;
      if (keyFilter) keyFilter.value = '';
      this.renderTree(this.CURRENT);
      this.renderMind(this.CURRENT);
    });

    // Tabs
    document.querySelectorAll('.tab').forEach(tab => {
      tab.addEventListener('click', () => {
        document.querySelectorAll('.tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        const target = (tab as HTMLElement).dataset['tab'];
        const treeTab = document.getElementById('treeTab');
        const mindTab = document.getElementById('mindTab');
        if (target === 'treeTab') { if (treeTab) treeTab.style.display = 'flex'; if (mindTab) mindTab.style.display = 'none'; }
        else { if (treeTab) treeTab.style.display = 'none'; if (mindTab) mindTab.style.display = 'flex'; this.renderMind(this.CURRENT ?? this.ROOT); }
      });
    });

    // File load
    fileInput?.addEventListener('change', e => {
      const f = (e.target as HTMLInputElement).files?.[0]; if (!f) return;
      const r = new FileReader();
      r.onload = () => {
        try { this.ROOT = JSON.parse(String(r.result)); this.CURRENT = this.ROOT; this.renderTree(this.ROOT); this.renderMind(this.ROOT); }
  catch (err) { alert('Invalid JSON: ' + (err as Error).message); }
      };
      r.readAsText(f);
    });

    // Drag & drop
    const dragOver = (ev: Event) => ev.preventDefault();
    const drop = (e: DragEvent) => {
      e.preventDefault(); const f = e.dataTransfer?.files?.[0]; if (!f) return;
  const r = new FileReader(); r.onload = () => { try { this.ROOT = JSON.parse(String(r.result)); this.CURRENT = this.ROOT; this.renderTree(this.ROOT); this.renderMind(this.ROOT);} catch (err) { alert('Invalid JSON: ' + (err as Error).message); } }; r.readAsText(f);
    };
    window.addEventListener('dragover', dragOver);
    window.addEventListener('drop', drop);
    this.destroyFns.push(() => { window.removeEventListener('dragover', dragOver); window.removeEventListener('drop', drop); });

    // Mindmap filters
    nameDescOnly?.addEventListener('click', () => { this.MINDMAP_FILTERS.mode = 'nameDescOnly'; this.renderMind(this.CURRENT ?? this.ROOT); });
    showEverything?.addEventListener('click', () => { this.MINDMAP_FILTERS.mode = 'everything'; this.renderMind(this.CURRENT ?? this.ROOT); });
  }
}
